/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */
import React, { useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import { ApiContext } from '../../../../context/ApiContext';
import descriptors from '../../../../locales/en-US/descriptors';
import { IRequestContext } from '../../IEnergyManagementProps';
import { Tooltip, Card } from '@scuf/common';
import { PollingContext } from '../../../../context/PollingContext/PollingContext';
import { returnFormattedDateTime } from '../../../shared/Utils/Utils';
import {
  convertValue,
  handleNumberUpscaling
} from '../../../shared/Utils/unitHandling/unitHandling';
import {
  getEuiUnitDisplayName,
  handleEuiUnitConversion
} from '../../../shared/Utils/unitHandling/EuiUnitHandling';
import {
  euiDefaultUnit,
  KPI_VALUE_DECIMAL_PRECISION,
  Value
} from '../../../shared/Utils/Constants';
import AccountWarning from '../../../../images/AccountWarning.svg';
import { getCommonEUIContent } from '../../../shared/Utils/EUI/EUIUtils';

interface IPortfolioDashboardSummaryContentProps {
  requestContext: IRequestContext;
}

const PortfolioDashboardSummaryContent = (
  props: IPortfolioDashboardSummaryContentProps
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const apiContext = useContext(ApiContext);
  const {
    organizationName,
    preferredEuiUnit,
    selectedDateFormat,
    selectedTimeFormat,
    userPreferedUnit,
    totalFootPrintNativeUnit
  } = apiContext;

  const summaryContent = (
    _convertedEuiContent: string,
    _convertedEuiUnit: string,
    _areaPerSQft: number | string,
    _message: string,
    footPrintUnit?: string
  ) => {
    return (
      <div className="emaas-sitedashboard-summary-values-container">
        {/* 2nd Column: Total Savings */}
        <div className="portfolio-summary-value-and-icon-container">
          <div className="emaas-sitedashboard-summary-value-and-label-container">
            <div className="emaas-sitedashboard-summary-valueWithPercentage">
              <div className="emaas-sitedashboard-summary-footprint-valueWithUnit eui">
                <span className="emaas-sitedashboard-summary-value">
                  {!!_message && (
                    <Tooltip
                      element={
                        <img
                          className="img-account-warning"
                          src={AccountWarning}
                        />
                      }
                      event="hover"
                      content={_message}
                      position="top center"
                      size="small"
                    />
                  )}
                  {_convertedEuiContent === '0' ? '--' : _convertedEuiContent}
                </span>
                <span className="emaas-sitedashboard-summary-footprint-unit">
                  {_convertedEuiContent !== '0' && _convertedEuiUnit}
                </span>
              </div>
            </div>

            {getCommonEUIContent(
              t(d.EUITAB),
              t(d.PORTFOLIOSUMMARYEUITOOLTIPHEADER),
              t(d.PORTFOLIOSUMMARYEUITOOLTIP)
            )}
          </div>
        </div>

        {/* 3rd Column: Total footprint */}
        <div className="portfolio-summary-value-and-icon-container">
          <div className="summary-image">
            <img
              src={require('../../../../images/TotalFootprint_icon.svg')}
              alt=""
            />
          </div>

          <div className="emaas-sitedashboard-summary-value-and-label-container">
            <div className="emaas-sitedashboard-summary-footprint-valueWithUnit">
              <span className="emaas-sitedashboard-summary-value">
                {_areaPerSQft}
              </span>
              {_areaPerSQft !== '--' && (
                <span className="emaas-sitedashboard-summary-footprint-unit">
                  {footPrintUnit}
                </span>
              )}
            </div>
            <div className="emaas-sitedashboard-summary-label">
              {t(d.TOTALFOOTSPRINT)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const pollingContext = useContext(PollingContext);
  const { lastPollingTime, euiSummary } = pollingContext;

  const convertedEuiContent = `${parseFloat(
    handleEuiUnitConversion(
      euiSummary?.totalEuiConsumption || 0,
      euiDefaultUnit,
      preferredEuiUnit || ''
    )?.toFixed(KPI_VALUE_DECIMAL_PRECISION)
  )}`;

  const convertedEuiUnit = getEuiUnitDisplayName(preferredEuiUnit);

  // function handleNumberUpscaling(
  //   number: number|string,
  //   targetUnit: string,
  //   nativeUnit: string
  // ): number {
  //   // Perform the necessary calculations
  //   let upscaledTotalFootprint = 1000; // Replace with actual upscaling logic

  //   // Round to 2 decimal places
  //   const roundedNumber = Math.round(upscaledTotalFootprint * 100) / 100;

  //   return roundedNumber;
  // }

  const upscaledTotalFootprint = handleNumberUpscaling(
    euiSummary?.totalEuiArea || '',
    userPreferedUnit === 'kilowatt hour' ? 'square meter' : 'square foot',
    totalFootPrintNativeUnit
  );

  const footPrintUnit = userPreferedUnit === 'kilowatt hour' ? 'sqm' : 'sq.ft';

  return (
    <Card>
      <Card.Content>
        <div className="emaas-sitedashboard-summary-container-div">
          <div className="emaas-sitedashboard-summary-header">
            {organizationName} - {t(d.PORTFOLIOSUMMARYHEADER)}
            {props.requestContext.context &&
              props.requestContext.context.endTime && (
                <span className="emaas-sitedashboard-last-updated">
                  {t(d.PORTFOLIOSUMMARYHEADERLASTUPDATEDON, {
                    date: returnFormattedDateTime(
                      lastPollingTime,
                      selectedDateFormat,
                      selectedTimeFormat
                    )
                  })}
                </span>
              )}
          </div>
          {summaryContent(
            convertedEuiContent,
            convertedEuiUnit,
            upscaledTotalFootprint || '--',
            // upscaledTotalFootprint || '--',
            euiSummary?.message,
            footPrintUnit
          )}
        </div>
      </Card.Content>
    </Card>
  );
};

export default PortfolioDashboardSummaryContent;
